import { InteractionRequiredAuthError, IPublicClientApplication, SilentRequest } from "@azure/msal-browser";

import { applicationConfig } from "../environmentConfiguration";

type TokenRefreshConfiguration = Pick<typeof applicationConfig, "apiPermissionScopes">;

/**
 * TokenRefreshManager handles MSAL token refresh with request queuing.
 * Ensures only one token refresh happens at a time and queues requests during refresh.
 */
export class TokenRefreshManager {
  private static instance: TokenRefreshManager;
  private refreshPromise: Promise<string | null> | null = null;

  private constructor(
    private msalInstance: IPublicClientApplication,
    private applicationConfig: TokenRefreshConfiguration,
  ) {}

  /**
   * Initialize the TokenRefreshManager singleton
   */
  static initialize(msalInstance: IPublicClientApplication, applicationConfig: TokenRefreshConfiguration): void {
    if (!TokenRefreshManager.instance) {
      TokenRefreshManager.instance = new TokenRefreshManager(msalInstance, applicationConfig);
    }
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): TokenRefreshManager {
    if (!TokenRefreshManager.instance) {
      throw new Error(
        "TokenRefreshManager not initialized. Call TokenRefreshManager.initialize() first in your auth provider.",
      );
    }
    return TokenRefreshManager.instance;
  }

  /**
   * Silently refresh the access token or return the existing promise if a refresh is in progress.
   * Returns the new access token or null if refresh failed.
   */
  async refreshAccessToken(): Promise<string | null> {
    // If already refreshing, return the existing promise to queue this request
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const account = this.msalInstance.getActiveAccount();

        if (!account) {
          console.warn("TokenRefreshManager: No active account found for token refresh");
          return null;
        }

        const request: SilentRequest = {
          scopes: this.applicationConfig.apiPermissionScopes,
          account,
        };

        const response = await this.msalInstance.acquireTokenSilent(request);
        console.debug("TokenRefreshManager: Access token refreshed silently");
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.error("TokenRefreshManager: Silent refresh failed - InteractionRequired. Trigger interactive login.");
          // Rethrow to let the caller handle interactive login
          throw error;
        }

        console.error("TokenRefreshManager: Silent refresh failed", error);
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Trigger interactive token acquisition as a fallback when silent refresh fails
   */
  async acquireTokenInteractive(): Promise<string | null> {
    try {
      const account = this.msalInstance.getActiveAccount();

      if (!account) {
        console.warn("TokenRefreshManager: No active account found for interactive token acquisition");
        return null;
      }

      const request: SilentRequest = {
        scopes: this.applicationConfig.apiPermissionScopes,
        account,
      };

      const response = await this.msalInstance.acquireTokenPopup(request);
      console.debug("TokenRefreshManager: Access token acquired via popup");
      return response.accessToken;
    } catch (error) {
      console.error("TokenRefreshManager: Interactive token acquisition failed", error);
      return null;
    }
  }

  /**
   * Trigger redirect-based token acquisition as an absolute fallback when popup fails.
   */
  async acquireTokenRedirectFallback(): Promise<void> {
    try {
      const account = this.msalInstance.getActiveAccount();

      if (!account) {
        console.warn("TokenRefreshManager: No active account found for redirect token acquisition");
        return;
      }

      const request: SilentRequest = {
        scopes: this.applicationConfig.apiPermissionScopes,
        account,
      };

      await this.msalInstance.acquireTokenRedirect(request);
    } catch (error) {
      console.error("TokenRefreshManager: Redirect token acquisition failed", error);
    }
  }
}
